import { convertUrlInBucket, convertUrlWithBucket } from '../../api/cdn.ts';
import type { IztUnit } from '../../api/types.ts';
import { normalizeYoutubeUrl } from '../../board/link/youtube.ts';
import { examIdOf } from '../../board/quiz/fromExam.ts';
import { contentKind } from '../../config/content-types.ts';
import type { YoutubeVideoReference } from '../../board/link/youtube.ts';
import type { VideoSource } from '../../board/video/plainMaster.ts';

export type UnitRenderable =
  | { kind: 'pdf'; url: string }
  | { kind: 'youtube'; video: YoutubeVideoReference }
  | { kind: 'video'; resourceId: string; source: VideoSource }
  | { kind: 'homework'; html: string }
  /** An IZ exam; the id is null when the unit names none, and the loader may still find it. */
  | { kind: 'test'; examId: string | null }
  | { kind: 'unsupported'; type: string };

// A bare ObjectId string means _populate never hydrated the resource, and grant-video needs the
// document: an id with no provider or status behind it plays nothing.
function populatedVideo(video: IztUnit['video']): VideoSource | null {
  if (!video || typeof video !== 'object') return null;
  const resourceId = String(video.id || video._id || '');
  if (!resourceId) return null;
  // what the cross-vendor fallback needs to rebuild the URL grant-video would have returned
  return { resourceId, vendorId: String(video.vendorId || ''), link: String(video.link || ''), isProtected: Boolean(video.origin && video.origin.isProtected) };
}

// homework task as CMS HTML; lms-api reads the same three places
function homeworkHtml(unit: IztUnit): string {
  const u = unit as unknown as { homeworkQuestion?: unknown; data?: { homeworkQuestion?: unknown }; settings?: { homeworkQuestion?: unknown } };
  const html = [u.homeworkQuestion, u.data && u.data.homeworkQuestion, u.settings && u.settings.homeworkQuestion].find((v) => typeof v === 'string' && v.trim().length > 0);
  return typeof html === 'string' ? html : '';
}

/** The homework's attached PDF, on the CDN its bucket names; an unpopulated origin falls back to the resource key. */
function homeworkPdf(unit: IztUnit): string {
  const pdf = unit.pdf as { link?: string; origin?: { link?: string; bucket?: string | null } } | undefined;
  if (!pdf || typeof pdf !== 'object') return '';
  const origin = pdf.origin || {};
  const link = origin.link || pdf.link || '';
  return link ? convertUrlInBucket(link, origin.bucket) : '';
}

// isUsePdf is the IZ switch for "the task is the PDF"; LTV prod homework also keeps a stub html beside it
function homeworkRenderable(unit: IztUnit, type: string): UnitRenderable {
  const pdf = homeworkPdf(unit);
  const usePdf = String((unit as { isUsePdf?: unknown }).isUsePdf) === 'true';
  if (pdf && usePdf) return { kind: 'pdf', url: pdf };
  const html = homeworkHtml(unit);
  if (html) return { kind: 'homework', html };
  if (pdf) return { kind: 'pdf', url: pdf };
  return { kind: 'unsupported', type };
}

export function getRenderable(unit: IztUnit): UnitRenderable {
  const type = String(unit.type || 'unknown');
  // externalLink is unvalidated CMS free text, so never frame it raw - a watch?v= URL renders blank
  const external = unit.settings && unit.settings.isExternalLink ? unit.externalLink : undefined;
  if (external) {
    const video = normalizeYoutubeUrl(external);
    if (video) return { kind: 'youtube', video };
  }
  // the manifest is a network grant, so this pure fork only reports that a resource is there
  const source = populatedVideo(unit.video);
  if (source) return { kind: 'video', resourceId: source.resourceId, source };
  const link = unit.pdf && unit.pdf.origin && unit.pdf.origin.link;
  if (type.toLowerCase() === 'pdf' && link) {
    return { kind: 'pdf', url: convertUrlWithBucket(link) };
  }
  if (type.toLowerCase() === 'homework') return homeworkRenderable(unit, type);
  // test, matrix and exercise units are their exam's questions, painted by the board itself
  if (contentKind(type) === 'assessment' || type.toLowerCase() === 'exercise') return { kind: 'test', examId: examIdOf(unit) };
  return { kind: 'unsupported', type };
}
